import { resolveHeatmapDisplayMode, countUnplottedHeatmapSamples } from '../../helpers/reports/heatmapCoordinates';
import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { Row, Col, Typography, Spin, Space, Alert } from 'antd';
import axios from 'axios';
import { ReportHeatmap } from './ReportHeatmap';
import { buildFileUrl, ENDPOINTS } from '../../helpers/api/apiConfig';
import { AreaLink } from '../AreaLink';
import { MetricLink } from '../MetricLink';
import { resolveColorRangesForMetric, resolveFieldRange } from '../../helpers/metricRules';

const { Text } = Typography;

interface CommonSectionProps {
    section: any;           // The section config from backend (with all nested objects as-is)
    data: any[];            // Raw data from API endpoint (DEPRECATED - now fetched per area)
    areas: any[];           // All areas
    areaTypes: any[];       // All area types  
    metrics: any[];         // All metrics
    previewAreaId: number;  // Currently selected area for preview
    dateRange?: any; // Date range for API calls (from/to or range tuple)
    onAreaClick?: (areaId: number) => void; // Callback for area navigation
    onMetricClick?: (metricKey: string, metricId: number | undefined, areaId: number) => void; // Callback for metric navigation
}

export const ReportHeatmapSection: React.FC<CommonSectionProps> = ({
    section,
    data, // DEPRECATED - keeping for backward compatibility but not used
    areas,
    areaTypes,
    metrics,
    previewAreaId,
    dateRange,
    onAreaClick,
    onMetricClick,
}) => {
    // State for per-area API data
    const [areaDataMap, setAreaDataMap] = useState<Map<number, any[]>>(new Map());
    const [loading, setLoading] = useState(false);

    // Build lookups
    const areaTypeById = useMemo(() => {
        const map = new Map<number, any>();
        areaTypes.forEach((at) => map.set(at.id, at));
        return map;
    }, [areaTypes]);

    const areaById = useMemo(() => {
        const map = new Map<number, any>();
        areas.forEach((a: any) => map.set(a.id, a));
        return map;
    }, [areas]);

    const childrenMap = useMemo(() => {
        const map = new Map<number, number[]>();
        areas.forEach((a: any) => {
            if (a.parentAreaId) {
                if (!map.has(a.parentAreaId)) map.set(a.parentAreaId, []);
                map.get(a.parentAreaId)!.push(a.id);
            }
        });
        return map;
    }, [areas]);

    const metricByKey = useMemo(() => {
        const map = new Map<string, any>();
        metrics.forEach((metric: any) => {
            if (metric?.key) map.set(metric.key, metric);
        });
        return map;
    }, [metrics]);

    // Resolve template info for an area (memoized with useCallback to prevent infinite loops)
    const resolveTemplateForArea = useCallback((area: any) => {
        if (!area) return { mode: 'gps' as const };
        
        const atId = area.areaType?.id || area.areaTypeId;
        const at = atId ? areaTypeById.get(atId) : null;
        const hasTemplateAssets = at && ((at as any).templateImage || (at as any).templateLinesImage);
        
        if (!hasTemplateAssets) {
            return { mode: 'gps' as const };
        }
        
        const dimensions = (at as any)?.templateDimensions;
        const templateImg = (at as any)?.templateImage;
        const templateLinesImg = (at as any)?.templateLinesImage;
        
        const templateImageUrl = atId
            ? `${ENDPOINTS.AREA_TYPES_V2}/${atId}/template-image`
            : templateImg
            ? templateImg.startsWith('http')
                ? templateImg
                : buildFileUrl(templateImg)
            : undefined;
        
        const templateLinesUrl = atId && templateLinesImg
            ? `${ENDPOINTS.AREA_TYPES_V2}/${atId}/template-lines-image`
            : undefined;
        
        return {
            mode: resolveHeatmapDisplayMode([], at),
            templateSize: dimensions || { width: 50, height: 70 },
            templateImageUrl,
            templateLinesUrl,
        };
    }, [areaTypeById]);

    // Build list of heatmaps to render based on section config
    const heatmapList = useMemo(() => {
        const list: Array<{
            label: string;
            level: number;
            mode: 'template' | 'gps';
            templateImageUrl?: string;
            templateLinesUrl?: string;
            templateSize?: any;
            areaId?: number;
            areaName?: string;
        }> = [];

        const excludeParent = section.childHeatmap?.excludeParent ?? false;
        const includeChildren = section.childHeatmap?.include ?? false;
        const depthLimit = section.childHeatmap?.depth ?? 1;

        // Add parent area if not excluded
        if (!excludeParent && previewAreaId) {
            const parentArea = areaById.get(previewAreaId);
            const tpl = resolveTemplateForArea(parentArea);
            list.push({
                label: parentArea?.name || 'Parent area',
                level: 0,
                mode: tpl.mode,
                templateImageUrl: tpl.templateImageUrl,
                templateLinesUrl: tpl.templateLinesUrl,
                templateSize: tpl.templateSize,
                areaId: previewAreaId,
                areaName: parentArea?.name,
            });
        }

        // Add child areas if included
        if (includeChildren && previewAreaId) {
            const queue: Array<{ id: number; level: number }> = (childrenMap.get(previewAreaId) || []).map((id) => ({
                id,
                level: 1,
            }));
            
            while (queue.length) {
                const { id, level } = queue.shift()!;
                if (level > depthLimit) continue;
                
                const child = areaById.get(id);
                const tpl = resolveTemplateForArea(child);
                list.push({
                    label: child?.name || `Child depth ${level}`,
                    level,
                    mode: tpl.mode,
                    templateImageUrl: tpl.templateImageUrl,
                    templateLinesUrl: tpl.templateLinesUrl,
                    templateSize: tpl.templateSize,
                    areaId: id,
                    areaName: child?.name,
                });
                
                // Add this area's children to queue
                (childrenMap.get(id) || []).forEach((cid) => queue.push({ id: cid, level: level + 1 }));
            }
        }

        return list;
    }, [section, previewAreaId, areaById, childrenMap, areaTypeById, resolveTemplateForArea]);

    // Create stable key from heatmap list to prevent infinite loops
    const heatmapListKey = useMemo(() => {
        return heatmapList.map(h => h.areaId).sort().join(',');
    }, [heatmapList]);

    // Create stable date range key
    const dateRangeKey = useMemo(() => {
        if (!dateRange) return '';
        if (dateRange.from && dateRange.to) {
            return `${dateRange.from}_${dateRange.to}`;
        }
        if (Array.isArray(dateRange) && dateRange.length === 2) {
            return `${dateRange[0]?.toString?.() ?? ''}_${dateRange[1]?.toString?.() ?? ''}`;
        }
        return '';
    }, [dateRange]);

    // Fetch data for each area individually with proper gridResolution
    useEffect(() => {
        if (!dateRange || !section.metrics || heatmapList.length === 0) {
            return;
        }

        const hexagonSize = section.hexagonSizeMeters ?? 50;
        const fetchHeatmapData = async () => {
            setLoading(true);
            try {
                const fetchPromises = heatmapList.map(async (item) => {
                    if (!item.areaId) return { areaId: null, data: [] };

                    const area = areaById.get(item.areaId);
                    const areaType = area?.areaType?.id ? areaTypeById.get(area.areaType.id) : null;

                    const payload: any = {
                        areaIds: [item.areaId],
                        dateRange: Array.isArray(dateRange) && dateRange.length === 2
                            ? {
                                  from: dateRange[0]?.toISOString?.() ?? dateRange[0],
                                  to: dateRange[1]?.toISOString?.() ?? dateRange[1],
                              }
                            : dateRange,
                        metrics: section.metrics,
                        hexagonSizeMeters: hexagonSize,
                    };

                    // Supply resolution for normalized history; backend ignores it for GPS frames.
                    {
                        if ((areaType as any)?.templateDimensions) {
                            payload.templateSize = (areaType as any).templateDimensions;
                        }
                        payload.gridResolutionX = section.heatmapGridResolutionX || 30;
                        if (section.heatmapGridResolutionY) {
                            payload.gridResolutionY = section.heatmapGridResolutionY;
                        }
                    }

                    console.log(`🚀 [ReportHeatmapSection] Fetching data for area ${item.areaId} (${item.areaName}):`, {
                        mode: item.mode,
                        gridResX: payload.gridResolutionX,
                        gridResY: payload.gridResolutionY,
                    });

                    const res = await axios.post(ENDPOINTS.REPORT_SECTION_HEATMAP, payload, {
                        withCredentials: true,
                    });

                    const responseData = res.data?.data || res.data || [];
                    console.log(`✅ [ReportHeatmapSection] Received data for area ${item.areaId}:`, {
                        dataLength: responseData.length,
                        firstItem: responseData[0],
                        hasGrid: !!responseData[0]?.grid,
                    });

                    return { areaId: item.areaId, data: responseData };
                });

                const results = await Promise.all(fetchPromises);
                const newMap = new Map<number, any[]>();
                results.forEach(r => {
                    if (r.areaId !== null) {
                        newMap.set(r.areaId, r.data);
                    }
                });

                setAreaDataMap(newMap);
            } catch (error) {
                console.error('[ReportHeatmapSection] Error fetching heatmap data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchHeatmapData();
    }, [heatmapListKey, dateRangeKey, section.metrics, section.heatmapGridResolutionX, section.heatmapGridResolutionY, section.hexagonSizeMeters, heatmapList, areaById, areaTypeById]);

    // Process fetched data into heatmap format grouped by area
    const heatmapByArea = useMemo(() => {
        const map = new Map<number, { unit?: string; grid?: any; points: any[]; average?: number; mode?: 'gps' | 'template'; unplottedPointCount?: number }>();
        
        areaDataMap.forEach((areaData, areaId) => {
            if (!Array.isArray(areaData)) return;
            
            const unplottedCount = countUnplottedHeatmapSamples(areaData);
            areaData.forEach((d: any) => {
                if (!d.areaId) return;
                
                if (!map.has(d.areaId)) {
                    map.set(d.areaId, { unit: d.unit, points: [], grid: undefined, average: d.average });
                }
                
                const entry = map.get(d.areaId)!;
                entry.mode = d.mode || entry.mode;
                entry.unplottedPointCount = unplottedCount;
                entry.unit = d.unit || entry.unit;
                entry.average = d.average ?? entry.average;

                // Store grid if available
                if (d.grid && d.grid.resolutionX && d.grid.resolutionY && Array.isArray(d.grid.cells)) {
                    entry.grid = d.grid;
                }

                // Collect raw points for hexagon binning
                if (d.gpsLat !== null && d.gpsLat !== undefined && 
                    d.gpsLon !== null && d.gpsLon !== undefined && 
                    d.average !== undefined) {
                    entry.points.push({
                        x: d.xLocal ?? 0,
                        y: d.yLocal ?? 0,
                        value: d.average,
                        gpsLat: d.gpsLat,
                        gpsLon: d.gpsLon,
                    });
                } else if (d.xLocal !== null && d.xLocal !== undefined &&
                           d.yLocal !== null && d.yLocal !== undefined &&
                           d.average !== undefined) {
                    entry.points.push({
                        x: d.xLocal,
                        y: d.yLocal,
                        value: d.average,
                        gpsLat: d.gpsLat,
                        gpsLon: d.gpsLon,
                    });
                }
            });
        });
        
        return map;
    }, [areaDataMap]);

    // Extract visualization config from section
    const templateOpacity = (section.templateOpacity ?? 70) / 100;
    const heatmapOpacity = (section.heatmapOpacity ?? 90) / 100;
    const colorScheme = (section.heatmapColorScheme as any) ?? 'blue-red';
    const invertColors = section.invertColors ?? false;
    const hexagonSizeMeters = section.hexagonSizeMeters ?? 50;
    const heatmapSpan = section.heatmapLayout?.span ?? section.heatmapSpan ?? 24;
    const childHeatmapSpan = section.heatmapLayout?.childSpan ?? section.childHeatmapSpan ?? 24;
    
    const fixedValueRange = section.heatmapValueRangeMode === 'fixed' &&
        section.heatmapValueMin !== null &&
        section.heatmapValueMax !== null
        ? {
              mode: 'fixed' as const,
              min: section.heatmapValueMin,
              max: section.heatmapValueMax,
          }
        : { mode: 'auto' as const };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin tip="Loading heatmaps..." />
            </div>
        );
    }

    if (heatmapList.length === 0) {
        return (
            <Text type="secondary">
                No heatmaps to display. Check area selection and configuration.
            </Text>
        );
    }

    return (
        <Row gutter={[12, 12]}>
            {heatmapList.map((item, idx) => {
                const areaData = item.areaId ? heatmapByArea.get(item.areaId) : undefined;
                const span = item.level === 0 ? heatmapSpan : childHeatmapSpan;
                const hasGridValues = !!areaData?.grid?.cells?.some(
                    (cell: any) => cell.value !== null && cell.value !== undefined
                );
                const hasPointValues = !!areaData?.points?.some(
                    (point: any) => point.value !== null && point.value !== undefined
                );
                const displayMode = areaData?.mode || item.mode;
                const unplottedCount = areaData?.unplottedPointCount || 0;
                const hasDataByMode = displayMode === 'template' ? hasGridValues : hasPointValues;
                if (!hasDataByMode && !unplottedCount) {
                    return null;
                }
                
                // Get geoJSON for this area
                const area = item.areaId ? areas.find((a: any) => a.id === item.areaId) : undefined;
                const metricKey = section.metrics?.[0];
                const metricConfig = metricKey ? metricByKey.get(metricKey) : null;
                const targetArea = area || (previewAreaId ? areaById.get(previewAreaId) : null);
                const areaDefaultRange = resolveFieldRange(
                    resolveColorRangesForMetric(metricConfig, targetArea),
                    'value'
                );
                const valueRange = section.heatmapValueRangeMode === 'areaDefault' &&
                    areaDefaultRange &&
                    areaDefaultRange.min !== null &&
                    areaDefaultRange.min !== undefined &&
                    areaDefaultRange.max !== null &&
                    areaDefaultRange.max !== undefined
                    ? {
                        mode: 'fixed' as const,
                        min: areaDefaultRange.min,
                        max: areaDefaultRange.max,
                    }
                    : fixedValueRange;
                const geoJsonPreview = area?.geom
                    ? {
                          type: 'FeatureCollection' as const,
                          features: [
                              {
                                  type: 'Feature' as const,
                                  geometry: area.geom,
                                  properties: {},
                              },
                          ],
                      }
                    : undefined;

                // Create clickable title if we have area ID and callback
                const titleNode = item.areaId && onAreaClick ? (
                    <AreaLink 
                        areaId={item.areaId} 
                        areaName={item.label} 
                        onClick={onAreaClick}
                    />
                ) : (
                    item.label
                );

                // Create metric links for this heatmap (specific to this area)
                const metricLinks = section.metrics && section.metrics.length > 0 && item.areaId ? (
                    <Space size="small">
                        {section.metrics.map((metricKey: string, metricIdx: number) => {
                            const metric = metrics.find(m => m.key === metricKey);
                            const metricName = metric?.name || metricKey;
                            
                            if (onMetricClick && item.areaId) {
                                return (
                                    <React.Fragment key={metricKey}>
                                        {metricIdx > 0 && <Text type="secondary" style={{ fontSize: 12 }}> · </Text>}
                                        <MetricLink
                                            metricKey={metricKey}
                                            metricName={metricName}
                                            metricId={metric?.id}
                                            areaId={item.areaId}
                                            onClick={onMetricClick}
                                            style={{ fontSize: 12 }}
                                        />
                                    </React.Fragment>
                                );
                            }
                            return (
                                <Text key={metricKey} style={{ fontSize: 12 }}>
                                    {metricIdx > 0 && ' · '}{metricName}
                                </Text>
                            );
                        })}
                    </Space>
                ) : null;

                return (
                    <Col span={span} key={`${item.label}-${idx}`}>
                        {unplottedCount > 0 && <Alert showIcon type="warning" style={{ marginBottom: 8 }}
                            message={`${unplottedCount} spatial samples could not be placed on this heatmap.`}
                            description="Their measurement values remain available in numeric summaries." />}
                        <ReportHeatmap
                            mode={displayMode}
                            templateSize={item.templateSize}
                            templateImageUrl={item.templateImageUrl}
                            templateLinesUrl={item.templateLinesUrl}
                            geoJsonPreview={geoJsonPreview}
                            title={titleNode}
                            subtitle={metricLinks}
                            value={
                                areaData?.average ??
                                (areaData?.points.length
                                    ? areaData.points.reduce((sum, p) => sum + (p.value || 0), 0) /
                                      areaData.points.length
                                    : undefined)
                            }
                            unit={areaData?.unit}
                            grid={item.mode === 'template' ? areaData?.grid : undefined}
                            dataPoints={
                                item.mode === 'gps' && areaData
                                    ? areaData.points.map((p) => ({
                                          x: Number(p.x ?? 0),
                                          y: Number(p.y ?? 0),
                                          value: Number(p.value ?? 0),
                                          gpsLat: p.gpsLat,
                                          gpsLon: p.gpsLon,
                                      }))
                                    : undefined
                            }
                            templateOpacity={templateOpacity}
                            heatmapOpacity={heatmapOpacity}
                            colorScheme={colorScheme}
                            invertColors={invertColors}
                            valueRange={valueRange}
                            hexagonSize={hexagonSizeMeters}
                            showCard={true}
                        />
                    </Col>
                );
            })}
        </Row>
    );
};

export default ReportHeatmapSection;
